import {
  Button,
  Col,
  DatePicker,
  Form,
  Radio,
  Row,
  Space,
  Typography,
  Input,
  message,
  Checkbox,
  Modal,
  Upload,
  Progress,
  Select,
  Alert,
  Spin,
} from "antd";
import { 
  UploadOutlined, 
  InboxOutlined, 
  FolderOutlined, 
  HomeOutlined, 
  RightOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FilePptOutlined,
  FileImageOutlined,
  FileZipOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  FolderOpenOutlined,
  FolderAddOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import InfraDetails from "components/common/InfraDetails";
import React, { useEffect, useState } from "react";
import {
  getProcurementDetails,
  updateProcurementStageItem,
  uploadFileToSharePoint,
  getSharePointFolderHierarchy,
  createSharePointFolder,
} from "apis";
import ProcurementStageDetailsFooter from "components/common/ProcurementStageDetailsFooter";
import { buildStageSK, PROCUREMENT_STAGES } from "types";

const { Text, Link } = Typography;
const { Dragger } = Upload;
const { Option } = Select;

// Enterprise styling
const style = {
  display: "flex",
  flexDirection: "column",
  gap: 20,
  fontWeight: 500,
};

const sectionCardStyle = {
  background: "#FFFFFF",
  border: "1px solid #E5E7EB",
  borderRadius: "12px",
  padding: "24px",
  marginBottom: "20px"
};

const SiteSection = ({ PK, isUserAllowed }) => {
  const [value, setValue] = useState(1);
  const [date, setDate] = useState(null);
  const [sharePointUrl, setSharePointUrl] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [siteStageData, setSiteStageData] = useState(null);

  const [spaceRequired, setSpaceRequired] = useState(false);
  const [coolingRequired, setCoolingRequired] = useState(false);
  const [powerRequired, setPowerRequired] = useState(false);
  const [requirementsChanged, setRequirementsChanged] = useState(false);

  const [fileManagerVisible, setFileManagerVisible] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedFolderId, setSelectedFolderId] = useState('root');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [folderHierarchy, setFolderHierarchy] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [currentPath, setCurrentPath] = useState([]);
  const [uploadCancelled, setUploadCancelled] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [uploadSummary, setUploadSummary] = useState({ success: 0, total: 0, files: [] });
  
  // New state for create folder
  const [createFolderModalVisible, setCreateFolderModalVisible] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  
  // Add ref to track if a folder load is in progress
  const loadingFolderRef = React.useRef(false);

  // Predefined folders for site assessments
  const assessmentFolders = [
    {
      label: "Site Assessments - Chris",
      value: "/sites/GIODCMOD/ROBO Site Details/Site Assessments/Chris",
      description: "For Chris to upload site assessment files",
    },
    {
      label: "Site Assessments - General",
      value: "/sites/GIODCMOD/ROBO Site Details/Site Assessments",
      description: "General site assessment documents",
    },
    {
      label: "LCR Assessment Files",
      value: "/sites/GIODCMOD/ROBO Site Details/LCR Assessments",
      description: "Local Computer Room assessment files",
    },
    {
      label: "Infrastructure Documents",
      value: "/sites/GIODCMOD/ROBO Site Details/Infrastructure",
      description: "Infrastructure related documents",
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getProcurementDetails(PK, "Site");
        if (response.success && response.data?.length) {
          const stageItem = response.data[0];
          console.log("Fetched site data:", stageItem);

          const url = stageItem.assessment_folder || "";
          setSharePointUrl(url);
          setEditMode(!url);
          setSiteStageData(stageItem);

          // Load requirement flags from DB
          const space = !!stageItem.spaceRequired;
          const cooling = !!stageItem.coolingRequired;
          const power = !!stageItem.powerRequired;

          setSpaceRequired(space);
          setCoolingRequired(cooling);
          setPowerRequired(power);

          // Auto-switch to "Site needs work" only if any requirement is true
          if (space || cooling || power) {
            setValue(2);
          } else {
            setValue(1);
          }
        } else {
          setEditMode(true);
        }
      } catch (error) {
        message.error("Failed to load procurement data.");
        setEditMode(true);
      }
    };

    fetchData();
  }, [PK]);

  useEffect(() => {
    if (value === 2) {
      const fetchRequirements = async () => {
        try {
          const response = await getProcurementDetails(PK, "Site");
          if (response.success && response.data?.length) {
            const stageItem = response.data[0];
            console.log("Fetched requirements data:", stageItem);

            setSpaceRequired(!!stageItem.spaceRequired);
            setCoolingRequired(!!stageItem.coolingRequired);
            setPowerRequired(!!stageItem.powerRequired);
            setRequirementsChanged(false);
          }
        } catch (error) {
          message.error("Failed to load requirements.");
        }
      };

      fetchRequirements();
    } else {
      setSpaceRequired(false);
      setCoolingRequired(false);
      setPowerRequired(false);
      setRequirementsChanged(false);
    }
  }, [value, PK]);

  const handleRadioOnChange = (e) => {
    setValue(e.target.value);
  };

  const disabledDate = (current) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return current && current < today;
  };

  const handleDataPickerChange = (date, dateString) => {
    setDate(dateString);
  };

  // Load SharePoint folder hierarchy with retry logic for cold start
  const loadFolderHierarchy = async (folderId = 'root', depth = 1) => {
    // Prevent multiple simultaneous calls
    if (loadingFolderRef.current) {
      console.log('Folder load already in progress, skipping...');
      return;
    }

    loadingFolderRef.current = true;
    setLoadingFolders(true);
    
    try {
      console.log(`Loading folder hierarchy for: ${folderId} with depth: ${depth}`);
      
      // Retry logic for Lambda cold start
      let loadResult = null;
      let retryCount = 0;
      const maxRetries = 3;
      
      while (retryCount < maxRetries && !loadResult) {
        try {
          const response = await getSharePointFolderHierarchy(folderId, depth);
          
          // Check if loading was successful
          // Accept the response if:
          // 1. It's successful AND
          // 2. Either has folders OR it's a retry (empty folder is valid on retry)
          if (response.success && response.data?.hierarchy !== undefined) {
            const hasData = response.data.hierarchy.length > 0;
            const isRetry = retryCount > 0;
            
            if (hasData || isRetry) {
              loadResult = response;
              console.log(`Successfully loaded ${response.data.hierarchy.length} folders on attempt ${retryCount + 1}`);
            } else {
              // First attempt returned empty - might be cold start
              retryCount++;
              if (retryCount < maxRetries) {
                const delay = Math.min(1000 * Math.pow(2, retryCount - 1), 3000);
                console.log(`First attempt returned empty, retrying in ${delay}ms (attempt ${retryCount}/${maxRetries})...`);
                await new Promise(resolve => setTimeout(resolve, delay));
              } else {
                // After all retries, accept empty result (folder is actually empty)
                loadResult = response;
                console.log('All retries completed, accepting empty result');
              }
            }
          } else if (response.error && (
            (typeof response.error === 'string' && response.error.includes('Event loop is closed')) ||
            (typeof response.error === 'object' && JSON.stringify(response.error).includes('Event loop is closed'))
          )) {
            // Specific handling for event loop error
            retryCount++;
            if (retryCount < maxRetries) {
              const delay = Math.min(1000 * Math.pow(2, retryCount - 1), 3000);
              console.log(`Event loop error detected for folder loading, retrying in ${delay}ms (attempt ${retryCount}/${maxRetries})...`);
              await new Promise(resolve => setTimeout(resolve, delay));
            } else {
              throw new Error('Event loop error - max retries exceeded');
            }
          } else {
            // Other error, don't retry
            const errorMsg = typeof response.error === 'string' 
              ? response.error 
              : 'Failed to load SharePoint folders';
            throw new Error(errorMsg);
          }
        } catch (loadError) {
          const errorMessage = loadError.message || String(loadError);
          
          // Check if it's an event loop error
          if (errorMessage.includes('Event loop is closed')) {
            retryCount++;
            if (retryCount < maxRetries) {
              const delay = Math.min(1000 * Math.pow(2, retryCount - 1), 3000);
              console.log(`Event loop error for folder loading, retrying in ${delay}ms (attempt ${retryCount}/${maxRetries})...`);
              await new Promise(resolve => setTimeout(resolve, delay));
            } else {
              throw new Error(`Event loop error - max retries (${maxRetries}) exceeded`);
            }
          } else {
            // Non-retryable error
            throw loadError;
          }
        }
      }

      if (!loadResult) {
        throw new Error('Folder loading failed after all retry attempts');
      }

      // Success - set the folder hierarchy
      setFolderHierarchy(loadResult.data.hierarchy);
      
    } catch (error) {
      const errorMsg = error.message || String(error) || 'Error loading folders';
      console.error('Folder loading error:', error);
      message.error('Error loading folders: ' + errorMsg);
      setFolderHierarchy([]);
    } finally {
      setLoadingFolders(false);
      loadingFolderRef.current = false;
    }
  };

  // Load initial folders when modal opens - with delay to prevent race condition
  useEffect(() => {
    if (fileManagerVisible) {
      setCurrentPath([{ id: 'root', name: 'ROBO/Commvault Site Details' }]);
      setSelectedFolder(null);
      setSelectedFolderId('root');
      
      // Small delay to ensure state is set before loading
      const timeoutId = setTimeout(() => {
        loadFolderHierarchy('root', 1);
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [fileManagerVisible]);

  // Handle folder navigation
  const handleFolderSelect = async (folder) => {
    if (uploading || loadingFolderRef.current) {
      message.warning('Please wait for the current operation to complete');
      return;
    }

    // Update current path and selected folder immediately
    const newPath = [...currentPath, { id: folder.id, name: folder.name }];
    setCurrentPath(newPath);
    setSelectedFolder({
      id: folder.id,
      name: folder.name,
      path: newPath.map(p => p.name).join(' / ')
    });
    setSelectedFolderId(folder.id);

    // Load subfolders for the selected folder with a small delay
    await new Promise(resolve => setTimeout(resolve, 100));
    await loadFolderHierarchy(folder.id, 1);
  };

  // Navigate back in folder path
  const handleNavigateBack = async (pathIndex) => {
    if (uploading || loadingFolderRef.current) {
      message.warning('Please wait for the current operation to complete');
      return;
    }

    const targetFolder = currentPath[pathIndex];
    const newPath = currentPath.slice(0, pathIndex + 1);
    
    setCurrentPath(newPath);
    setSelectedFolderId(targetFolder.id);
    setSelectedFolder({
      id: targetFolder.id,
      name: targetFolder.name,
      path: newPath.map(p => p.name).join(' / ')
    });
    
    // Add delay before loading
    await new Promise(resolve => setTimeout(resolve, 100));
    await loadFolderHierarchy(targetFolder.id, 1);
  };

  // Select current folder for upload
  const handleSelectCurrentFolder = () => {
    const currentFolderInfo = {
      id: selectedFolderId,
      name: currentPath[currentPath.length - 1]?.name || 'ROBO/Commvault Site Details',
      path: currentPath.map(p => p.name).join(' / ')
    };
    setSelectedFolder(currentFolderInfo);
    message.success(`Selected folder: ${currentFolderInfo.name}`);
  };

  const handleFileUpload = async () => {
    if (!selectedFolder || !selectedFolder.id) {
      message.warning("Please select a destination folder first");
      return;
    }

    if (selectedFiles.length === 0) {
      message.warning("Please select files to upload");
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadCancelled(false);
    setUploadComplete(false);

    const uploadedUrls = [];
    const uploadedFiles = [];
    let hasErrors = false;

    // Create a stable copy of files array to prevent reference issues
    const filesToUpload = [...selectedFiles];

    try {
      for (let i = 0; i < filesToUpload.length; i++) {
        if (uploadCancelled) {
          message.warning('Upload cancelled by user');
          break;
        }

        const file = filesToUpload[i];
        const fileName = file.name;
        const fileSize = file.size;

        try {
          // Update progress before upload
          const progressBefore = Math.round(((i * 100) / filesToUpload.length));
          setUploadProgress(progressBefore);

          console.log(`Starting upload for file: ${fileName} (${fileSize} bytes)`);

          // Convert file to base64 - ensure file object is fresh
          let fileContentBase64;
          try {
            fileContentBase64 = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              
              const handleLoad = () => {
                try {
                  if (reader.result) {
                    const base64String = reader.result.split(',')[1];
                    console.log(`File ${fileName} converted to base64 successfully`);
                    resolve(base64String);
                  } else {
                    reject(new Error('FileReader result is null'));
                  }
                } catch (error) {
                  reject(new Error(`Error processing file data: ${error.message}`));
                }
              };
              
              const handleError = () => {
                reject(new Error(`FileReader error: ${reader.error?.message || 'Unknown error'}`));
              };
              
              reader.onload = handleLoad;
              reader.onerror = handleError;
              reader.onabort = () => reject(new Error('File reading was aborted'));

              // Start reading immediately
              reader.readAsDataURL(file);
            });
          } catch (readError) {
            console.error(`Error reading file ${fileName}:`, readError);
            throw new Error(`Could not read file: ${readError.message}`);
          }

          if (!fileContentBase64) {
            throw new Error('File conversion resulted in empty data');
          }

          console.log(`Uploading ${fileName} to SharePoint folder: ${selectedFolder.id}`);

          // Upload to SharePoint with retry logic to handle Lambda cold start
          let uploadResult = null;
          let retryCount = 0;
          const maxRetries = 3;
          
          while (retryCount < maxRetries && !uploadResult) {
            try {
              const result = await uploadFileToSharePoint(
                selectedFolder.id,
                fileName,
                fileContentBase64
              );

              // Check if upload was successful
              if (result.success && result.data?.success) {
                uploadResult = result;
                console.log(`Upload successful for ${fileName} on attempt ${retryCount + 1}`);
              } else if (result.error && result.error.includes('Event loop is closed')) {
                // Specific handling for event loop error
                retryCount++;
                if (retryCount < maxRetries) {
                  const delay = Math.min(1000 * Math.pow(2, retryCount - 1), 3000); // Exponential backoff: 1s, 2s, 3s
                  console.log(`Event loop error detected for ${fileName}, retrying in ${delay}ms (attempt ${retryCount}/${maxRetries})...`);
                  await new Promise(resolve => setTimeout(resolve, delay));
                } else {
                  throw new Error(result.error || 'Event loop error - max retries exceeded');
                }
              } else {
                // Other error, don't retry
                throw new Error(result.error || result.data?.error || 'Unknown upload error');
              }
            } catch (uploadError) {
              const errorMessage = uploadError.message || String(uploadError);
              
              // Check if it's an event loop error
              if (errorMessage.includes('Event loop is closed')) {
                retryCount++;
                if (retryCount < maxRetries) {
                  const delay = Math.min(1000 * Math.pow(2, retryCount - 1), 3000);
                  console.log(`Event loop error for ${fileName}, retrying in ${delay}ms (attempt ${retryCount}/${maxRetries})...`);
                  await new Promise(resolve => setTimeout(resolve, delay));
                } else {
                  throw new Error(`Event loop error - max retries (${maxRetries}) exceeded`);
                }
              } else {
                // Non-retryable error
                throw uploadError;
              }
            }
          }

          if (!uploadResult) {
            throw new Error('Upload failed after all retry attempts');
          }

          // Process successful upload
          uploadedUrls.push({
            fileName: fileName,
            sharePointUrl: uploadResult.data.web_url,
            webUrl: uploadResult.data.web_url,
          });

          uploadedFiles.push({
            file_id: uploadResult.data.file_id,
            file_name: uploadResult.data.file_name,
            web_url: uploadResult.data.web_url,
            download_url: uploadResult.data.download_url,
            uploaded_at: new Date().toISOString(),
            folder_path: selectedFolder.path,
          });

          // Update progress after successful upload
          const progressAfter = Math.round((((i + 1) * 100) / filesToUpload.length));
          setUploadProgress(progressAfter);
          
          message.success(`Uploaded: ${fileName}`, 2);

        } catch (fileError) {
          console.error(`Error processing file ${fileName}:`, fileError);
          message.error(`Failed to upload ${fileName}: ${fileError.message}`, 5);
          hasErrors = true;
        }

        // Small delay between uploads to prevent overwhelming the API
        if (i < filesToUpload.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }

      // Update Site stage with uploaded file information
      if (uploadedFiles.length > 0) {
        try {
          const siteStageResponse = await getProcurementDetails(PK, "Site");
          if (siteStageResponse.success && siteStageResponse.data?.length) {
            const siteStage = siteStageResponse.data[0];
            
            const existingFiles = siteStage.uploaded_files || [];
            const updatedSiteStage = {
              ...siteStage,
              uploaded_files: [...existingFiles, ...uploadedFiles],
            };

            await updateProcurementStageItem(updatedSiteStage);
          }
        } catch (error) {
          console.error("Error updating Site stage with file metadata:", error);
          // Don't fail the whole upload if metadata save fails
        }
      }

      // Show upload completion modal
      if (uploadedFiles.length > 0) {
        setUploadSummary({
          success: uploadedFiles.length,
          total: filesToUpload.length,
          files: uploadedFiles
        });
        setUploadComplete(true);

        if (uploadedUrls.length > 0 && uploadedUrls[0].webUrl) {
          const folderUrl = uploadedUrls[0].webUrl.substring(0, uploadedUrls[0].webUrl.lastIndexOf('/'));
          setSharePointUrl(folderUrl);
          await handleSaveUrl(folderUrl);
        }
      } else {
        message.error('No files were uploaded successfully', 5);
      }
    } catch (error) {
      console.error("Upload error details:", error);
      message.error("Upload process failed: " + error.message, 5);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleCloseUploadComplete = () => {
    setUploadComplete(false);
    handleCloseFileManager();
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = () => {
        try {
          if (reader.result) {
            const base64String = reader.result.split(',')[1];
            resolve(base64String);
          } else {
            reject(new Error('FileReader result is null'));
          }
        } catch (error) {
          reject(new Error(`Error processing file data: ${error.message}`));
        }
      };
      
      reader.onerror = () => {
        reject(new Error(`FileReader error: ${reader.error?.message || 'Unknown error'}`));
      };
      
      reader.onabort = () => {
        reject(new Error('File reading was aborted'));
      };

      // Start reading the file immediately
      reader.readAsDataURL(file);
    });
  };

  const handleCloseFileManager = () => {
    if (uploading) {
      Modal.confirm({
        title: 'Upload in Progress',
        content: 'Are you sure you want to cancel the upload and close?',
        okText: 'Yes, Cancel Upload',
        okType: 'danger',
        cancelText: 'Continue Upload',
        onOk: () => {
          setUploadCancelled(true);
          setFileManagerVisible(false);
          setSelectedFolder(null);
          setSelectedFolderId('root');
          setSelectedFiles([]);
          setUploadProgress(0);
          setUploading(false);
          setFolderHierarchy([]);
          setCurrentPath([]);
          setUploadCancelled(false);
        }
      });
      return;
    }
    
    setFileManagerVisible(false);
    setSelectedFolder(null);
    setSelectedFolderId('root');
    setSelectedFiles([]);
    setUploadProgress(0);
    setUploading(false);
    setFolderHierarchy([]);
    setCurrentPath([]);
    setUploadCancelled(false);
  };

  const uploadProps = {
    multiple: true,
    showUploadList: true,
    beforeUpload: (file) => {
      setSelectedFiles((prev) => [...prev, file]);
      return false;
    },
    onRemove: (file) => {
      setSelectedFiles((prev) => prev.filter((f) => f.uid !== file.uid));
    },
    fileList: selectedFiles.map((file) => ({
      uid: file.uid || file.name,
      name: file.name,
      status: "done",
      size: file.size,
    })),
    itemRender: (originNode, file) => {
      const getFileIcon = (fileName) => {
        const ext = fileName.split('.').pop().toLowerCase();
        const iconStyle = { fontSize: '24px', color: '#00857c' };
        
        switch(ext) {
          case 'pdf': return <FilePdfOutlined style={iconStyle} />;
          case 'doc':
          case 'docx': return <FileWordOutlined style={iconStyle} />;
          case 'xls':
          case 'xlsx': return <FileExcelOutlined style={iconStyle} />;
          case 'ppt':
          case 'pptx': return <FilePptOutlined style={iconStyle} />;
          case 'jpg':
          case 'jpeg':
          case 'png':
          case 'gif': return <FileImageOutlined style={iconStyle} />;
          case 'zip':
          case 'rar': return <FileZipOutlined style={iconStyle} />;
          case 'txt': return <FileTextOutlined style={iconStyle} />;
          default: return <FileOutlined style={iconStyle} />;
        }
      };

      const formatFileSize = (bytes) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
      };

      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          padding: '12px 16px',
          marginBottom: '8px',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          transition: 'all 0.3s ease',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 133, 124, 0.15)';
          e.currentTarget.style.borderColor = '#00857c';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
          e.currentTarget.style.borderColor = '#e0e0e0';
        }}
        >
          <span style={{ marginRight: '12px' }}>{getFileIcon(file.name)}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ 
              fontWeight: 600, 
              color: '#333',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              marginBottom: '4px'
            }}>
              {file.name}
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {formatFileSize(file.size)}
            </div>
          </div>
          <Button
            type="text"
            danger
            size="small"
            onClick={() => uploadProps.onRemove(file)}
            style={{ marginLeft: '12px' }}
          >
            Remove
          </Button>
        </div>
      );
    },
  };

  const handleSaveUrl = async (urlToSave = sharePointUrl) => {
    try {
      const updatedStage = {
        ...(siteStageData || {
          PK: { S: PK },
          SK: { S: buildStageSK(PROCUREMENT_STAGES.Site) },
          stage_name: { S: "Site" },
          entity_type: { S: "ProcurementStage" },
        }),
        assessment_folder: urlToSave,
      };

      const response = await updateProcurementStageItem(updatedStage);
      if (response.success) {
        message.success("SharePoint URL saved.");
        setEditMode(false);
        setSiteStageData(updatedStage);
        setSharePointUrl(urlToSave);
      } else {
        message.error("Failed to save URL.");
      }
    } catch (error) {
      message.error("Error saving SharePoint URL.");
    }
  };

  const handleEditUrl = () => {
    setEditMode(true);
  };

  const handleSaveRequirements = async () => {
    try {
      const updatedStage = {
        ...(siteStageData || {
          PK: { S: PK },
          SK: { S: buildStageSK(PROCUREMENT_STAGES.Site) },
          stage_name: { S: "Site" },
          entity_type: { S: "ProcurementStage" },
        }),
        spaceRequired,
        coolingRequired,
        powerRequired,
      };

      const response = await updateProcurementStageItem(updatedStage);
      if (response.success) {
        message.success("Requirements saved.");
        setSiteStageData(updatedStage);
        setRequirementsChanged(false);
      } else {
        message.error("Failed to save requirements.");
      }
    } catch (error) {
      message.error("Error saving requirements.");
    }
  };

  const onSpaceRequiredChange = (e) => {
    setSpaceRequired(e.target.checked);
    setRequirementsChanged(true);
  };
  const onCoolingRequiredChange = (e) => {
    setCoolingRequired(e.target.checked);
    setRequirementsChanged(true);
  };
  const onPowerRequiredChange = (e) => {
    setPowerRequired(e.target.checked);
    setRequirementsChanged(true);
  };

  // Handle create folder
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      message.warning('Please enter a folder name');
      return;
    }

    // Validate folder name
    const invalidChars = /[<>:"/\\|?*]/;
    if (invalidChars.test(newFolderName)) {
      message.error('Folder name cannot contain: < > : " / \\ | ? *');
      return;
    }

    if (newFolderName.length > 255) {
      message.error('Folder name must be less than 255 characters');
      return;
    }

    setCreatingFolder(true);
    try {
      console.log(`Creating folder "${newFolderName}" in parent "${selectedFolderId}"`);
      
      // Retry logic for Lambda cold start
      let createResult = null;
      let retryCount = 0;
      const maxRetries = 3;
      
      while (retryCount < maxRetries && !createResult) {
        try {
          const response = await createSharePointFolder(selectedFolderId, newFolderName);
          
          // Check if folder creation was successful
          if (response.success && response.data?.success) {
            createResult = response;
            console.log(`Folder created successfully on attempt ${retryCount + 1}`);
          } else if (response.error && (
            (typeof response.error === 'string' && response.error.includes('Event loop is closed')) ||
            (typeof response.error === 'object' && JSON.stringify(response.error).includes('Event loop is closed'))
          )) {
            // Specific handling for event loop error
            retryCount++;
            if (retryCount < maxRetries) {
              const delay = Math.min(1000 * Math.pow(2, retryCount - 1), 3000); // Exponential backoff: 1s, 2s, 3s
              console.log(`Event loop error detected, retrying in ${delay}ms (attempt ${retryCount}/${maxRetries})...`);
              message.info(`Warming up SharePoint connection, retrying...`, 2);
              await new Promise(resolve => setTimeout(resolve, delay));
            } else {
              throw new Error('Event loop error - max retries exceeded');
            }
          } else {
            // Other error, don't retry
            const errorMessage = typeof response.data?.error === 'string' 
              ? response.data.error 
              : typeof response.error === 'string'
              ? response.error
              : JSON.stringify(response.data?.error || response.error || 'Failed to create folder');
            throw new Error(errorMessage);
          }
        } catch (createError) {
          const errorMessage = createError.message || String(createError);
          
          // Check if it's an event loop error
          if (errorMessage.includes('Event loop is closed')) {
            retryCount++;
            if (retryCount < maxRetries) {
              const delay = Math.min(1000 * Math.pow(2, retryCount - 1), 3000);
              console.log(`Event loop error for folder creation, retrying in ${delay}ms (attempt ${retryCount}/${maxRetries})...`);
              message.info(`Warming up SharePoint connection, retrying...`, 2);
              await new Promise(resolve => setTimeout(resolve, delay));
            } else {
              throw new Error(`Event loop error - max retries (${maxRetries}) exceeded`);
            }
          } else {
            // Non-retryable error
            throw createError;
          }
        }
      }

      if (!createResult) {
        throw new Error('Folder creation failed after all retry attempts');
      }

      // Success!
      message.success(`Folder "${newFolderName}" created successfully!`);
      setNewFolderName('');
      setCreateFolderModalVisible(false);
      
      // Wait a bit before refreshing to ensure SharePoint has synced
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Refresh folder hierarchy to show new folder
      await loadFolderHierarchy(selectedFolderId, 1);
      
    } catch (error) {
      console.error('Create folder error:', error);
      const errorMessage = error.message || String(error) || 'An error occurred while creating the folder';
      message.error(errorMessage, 5);
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleCancelCreateFolder = () => {
    setNewFolderName('');
    setCreateFolderModalVisible(false);
  };

  return (
    <Form disabled={!isUserAllowed}>
      <div
        id="site-section"
        style={{
          backgroundColor: "white",
          padding: "2% 2%",
          border: "3px solid #f6f6f6",
          borderRadius: "10px",
        }}
      >
        <InfraDetails PK={PK} />

        {/* DCIM Tool Link */}
        <Row style={{ marginTop: "20px" }}>
          <Col span={24}>
            <Text strong>DCIM Tool: </Text>
            <Link href="https://dctrack.merck.com/dcim/#/items" target="_blank">
              https://dctrack.merck.com/dcim/#/items
            </Link>
          </Col>
        </Row>

        {/* Radio & ETA */}
        <Row style={{ marginTop: "20px" }}>
          <Col span={24}>
            <Radio.Group
              style={style}
              onChange={handleRadioOnChange}
              value={value}
            >
              <Radio value={1}>Site can support the additional resources</Radio>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10%",
                }}
              >
                <Radio value={2}>
                  Site needs work to support the additional resources
                </Radio>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <Typography>Select ETA : </Typography>
                  <DatePicker
                    disabled={value !== 2}
                    disabledDate={disabledDate}
                    onChange={handleDataPickerChange}
                    placeholder="Select Date"
                  />
                </div>
              </div>
            </Radio.Group>
          </Col>
        </Row>

        {value === 2 && (
          <Row>
            <Col span={24}>
              <div
                style={{
                  border: "1px dashed #999",
                  padding: "20px",
                  marginTop: "10px",
                }}
              >
                <Text strong>Local Computer Room : </Text>

                <Checkbox
                  checked={spaceRequired}
                  onChange={onSpaceRequiredChange}
                  style={{
                    marginTop: "10px",
                    marginLeft: "10px",
                  }}
                >
                  More Space Required
                </Checkbox>

                <Checkbox
                  checked={coolingRequired}
                  onChange={onCoolingRequiredChange}
                >
                  More Cooling Required
                </Checkbox>

                <Checkbox
                  checked={powerRequired}
                  onChange={onPowerRequiredChange}
                >
                  More Power Required
                </Checkbox>

                <Button
                  type="primary"
                  onClick={handleSaveRequirements}
                  disabled={!requirementsChanged}
                  style={{
                    marginTop: "15px",
                    marginLeft: "25px",
                  }}
                >
                  Save Requirements
                </Button>
              </div>
            </Col>
          </Row>
        )}

        {/* SharePoint URL Section */}
        <Row style={{ marginTop: "30px" }}>
          <Col span={24}>
            <Form layout="inline">
              {editMode ? (
                <>
                  <Form.Item label="SharePoint Location -">
                    <Input
                      placeholder="Enter SharePoint URL for Assessment folder"
                      value={sharePointUrl}
                      onChange={(e) => setSharePointUrl(e.target.value)}
                      style={{ width: 400 }}
                    />
                  </Form.Item>
                  <Form.Item>
                    <Space>
                      <Button type="primary" onClick={() => handleSaveUrl()}>
                        Save
                      </Button>
                      <Button
                        type="default"
                        onClick={() => setFileManagerVisible(true)}
                        icon={<UploadOutlined />}
                      >
                        Upload Files
                      </Button>
                    </Space>
                  </Form.Item>
                </>
              ) : (
                <Space>
                  <Text strong>LCR Assessment file Location: </Text>
                  <Link
                    href={sharePointUrl}
                    target="_blank"
                    style={{
                      maxWidth: "400px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "inline-block",
                    }}
                  >
                    {sharePointUrl}
                  </Link>
                  <Button onClick={handleEditUrl}>Edit</Button>
                  <Button
                    type="default"
                    onClick={() => setFileManagerVisible(true)}
                    icon={<UploadOutlined />}
                  >
                    Upload Files
                  </Button>
                </Space>
              )}
            </Form>
            <div style={{ marginTop: "10px" }}>
              <Link
                href="https://collaboration.merck.com/sites/GIODCMOD/ROBO%20Site%20Details/Forms/AllItems.aspx?csf=1&web=1&e=EUYEkl&cid=30f97aaa%2D4a69%2D45b9%2D9446%2D88fca90b7c32&viewid=0d79b257%2D18a1%2D4354%2Db262%2D6a3365995e67"
                target="_blank"
              >
                Go to Base Folder for getting Link
              </Link>
            </div>
          </Col>
        </Row>

        {/* SharePoint File Manager Modal */}
        <Modal
          title={
            <div style={{ 
              background: 'linear-gradient(135deg, #00857c 0%, #006b63 100%)',
              margin: '-20px -24px 0 -24px',
              padding: '24px',
              color: 'white',
              borderRadius: '8px 8px 0 0',
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              boxShadow: '0 4px 12px rgba(0, 133, 124, 0.2)'
            }}>
              <div style={{
                background: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '12px',
                padding: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <FolderOutlined style={{ fontSize: '28px' }} />
              </div>
              <div>
                <div style={{ fontSize: '20px', fontWeight: 700, marginBottom: '4px' }}>
                  SharePoint File Manager
                </div>
                <div style={{ fontSize: '13px', opacity: 0.9 }}>
                  Browse folders and upload your files securely
                </div>
              </div>
            </div>
          }
          open={fileManagerVisible}
          onCancel={handleCloseFileManager}
          width={1200}
          closable={!uploading}
          maskClosable={!uploading}
          styles={{ 
            body: {
              padding: '0',
              background: 'linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%)',
              height: '650px'
            }
          }}
          footer={
            <div style={{ 
              borderTop: '2px solid #e0e0e0',
              padding: '20px 24px',
              background: 'linear-gradient(to right, #ffffff, #f8f9fa)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {selectedFiles.length > 0 && (
                  <>
                    <div style={{
                      background: 'linear-gradient(135deg, #e6f7f6 0%, #d9f0ef 100%)',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <InboxOutlined style={{ fontSize: '20px', color: '#00857c' }} />
                      <div>
                        <div style={{ fontSize: '18px', fontWeight: 700, color: '#00857c' }}>
                          {selectedFiles.length}
                        </div>
                        <div style={{ fontSize: '11px', color: '#666', marginTop: '-2px' }}>
                          file{selectedFiles.length !== 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>
                    {selectedFolder && (
                      <div style={{ fontSize: '13px', color: '#666' }}>
                        Ready to upload to <strong style={{ color: '#00857c' }}>{selectedFolder.name}</strong>
                      </div>
                    )}
                  </>
                )}
              </div>
              <Space size="middle">
                <Button 
                  onClick={handleCloseFileManager} 
                  disabled={uploading} 
                  size="large"
                  style={{ minWidth: '100px' }}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  onClick={handleFileUpload}
                  loading={uploading}
                  disabled={!selectedFolder || selectedFiles.length === 0}
                  icon={<UploadOutlined />}
                  size="large"
                  style={{
                    background: 'linear-gradient(135deg, #00857c 0%, #006b63 100%)',
                    border: 'none',
                    boxShadow: '0 4px 12px rgba(0, 133, 124, 0.4)',
                    minWidth: '160px',
                    height: '48px',
                    fontSize: '16px',
                    fontWeight: 600
                  }}
                >
                  Upload {selectedFiles.length > 0 ? `${selectedFiles.length} File${selectedFiles.length !== 1 ? 's' : ''}` : ''}
                </Button>
              </Space>
            </div>
          }
        >
          {/* Breadcrumb Navigation */}
          <div style={{ 
            background: 'white',
            padding: '16px 24px',
            borderBottom: '2px solid #e6f7f6',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', flex: 1 }}>
              <div style={{
                background: 'linear-gradient(135deg, #00857c 0%, #006b63 100%)',
                borderRadius: '6px',
                padding: '6px 10px',
                display: 'flex',
                alignItems: 'center'
              }}>
                <HomeOutlined style={{ color: 'white', fontSize: '16px' }} />
              </div>
              {currentPath.map((folder, index) => (
                <React.Fragment key={folder.id}>
                  <RightOutlined style={{ fontSize: '12px', color: '#999' }} />
                  <Button 
                    type="link" 
                    onClick={() => handleNavigateBack(index)}
                    disabled={uploading}
                    style={{ 
                      padding: '6px 12px',
                      color: index === currentPath.length - 1 ? '#00857c' : '#666',
                      fontWeight: index === currentPath.length - 1 ? 600 : 400,
                      fontSize: '14px',
                      background: index === currentPath.length - 1 ? '#e6f7f6' : 'transparent',
                      borderRadius: '6px'
                    }}
                  >
                    {folder.name}
                  </Button>
                </React.Fragment>
              ))}
            </div>
            
            {/* Create Folder Button */}
            <Button
              type="primary"
              icon={<FolderAddOutlined />}
              onClick={() => setCreateFolderModalVisible(true)}
              disabled={uploading}
              style={{
                background: 'linear-gradient(135deg, #00857c 0%, #006b63 100%)',
                border: 'none',
                borderRadius: '8px',
                height: '40px',
                padding: '0 20px',
                fontSize: '14px',
                fontWeight: 600,
                boxShadow: '0 2px 8px rgba(0, 133, 124, 0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              New Folder
            </Button>
          </div>

          {/* Split Pane Layout */}
          <div style={{ display: 'flex', height: '530px' }}>
            {/* LEFT PANEL - Folder Browser */}
            <div style={{ 
              width: '45%',
              borderRight: '2px solid #e6f7f6',
              background: 'white',
              display: 'flex',
              flexDirection: 'column'
            }}>
              {/* Folder Header */}
              <div style={{
                background: 'linear-gradient(135deg, #f0f9f8 0%, #e6f7f6 100%)',
                padding: '16px 20px',
                borderBottom: '3px solid #00857c',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <Text strong style={{ color: '#00857c', fontSize: '16px', display: 'block', marginBottom: '2px' }}>
                    Browse Folders
                  </Text>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Navigate and select your destination
                  </Text>
                </div>
                {loadingFolders && <Spin size="small" style={{ color: '#00857c' }} />}
              </div>
              
              {/* Folder List */}
              <div style={{ 
                flex: 1,
                overflowY: 'auto',
                padding: '16px',
                background: '#fafbfc'
              }}>
                {loadingFolders && folderHierarchy.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '80px 20px' }}>
                    <Spin size="large" />
                    <div style={{ marginTop: '20px' }}>
                      <Text strong style={{ color: '#00857c' }}>Loading folders...</Text>
                    </div>
                  </div>
                ) : folderHierarchy.length === 0 ? (
                  <div style={{ padding: '80px 20px', textAlign: 'center' }}>
                    <div style={{
                      background: 'linear-gradient(135deg, #e6f7f6 0%, #d9f0ef 100%)',
                      borderRadius: '50%',
                      width: '100px',
                      height: '100px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 20px'
                    }}>
                      <FolderOpenOutlined style={{ fontSize: '48px', color: '#00857c' }} />
                    </div>
                    <div style={{ marginBottom: '12px' }}>
                      <Text strong style={{ fontSize: '16px', color: '#333' }}>No Subfolders</Text>
                    </div>
                    <Text type="secondary" style={{ fontSize: '13px', display: 'block', marginBottom: '24px' }}>
                      This folder is ready for your uploads
                    </Text>
                    <Button 
                      type="primary"
                      onClick={handleSelectCurrentFolder}
                      disabled={uploading}
                      icon={<UploadOutlined />}
                      size="large"
                      style={{
                        background: 'linear-gradient(135deg, #00857c 0%, #006b63 100%)',
                        border: 'none',
                        borderRadius: '8px',
                        height: '48px',
                        padding: '0 32px',
                        fontSize: '15px',
                        fontWeight: 600
                      }}
                    >
                      Use This Folder
                    </Button>
                  </div>
                ) : (
                  <div>
                    {/* Current Folder Selection */}
                    <div style={{ 
                      marginBottom: '20px',
                      padding: '16px',
                      background: 'linear-gradient(135deg, #e6f7f6 0%, #f0f9f8 100%)',
                      border: '2px solid #b3ddd9',
                      borderRadius: '10px',
                      boxShadow: '0 4px 12px rgba(0, 133, 124, 0.1)'
                    }}>
                      <Button 
                        block
                        onClick={handleSelectCurrentFolder}
                        disabled={uploading}
                        icon={<UploadOutlined />}
                        style={{ 
                          borderColor: '#00857c',
                          color: '#00857c',
                          height: '52px',
                          fontWeight: 700,
                          fontSize: '15px',
                          borderRadius: '8px',
                          background: 'white',
                          borderWidth: '2px'
                        }}
                      >
                        Use Current: {currentPath[currentPath.length - 1]?.name}
                      </Button>
                    </div>

                    <div style={{ 
                      marginBottom: '14px',
                      paddingBottom: '10px',
                      borderBottom: '2px solid #e6f7f6'
                    }}>
                      <Text strong style={{ 
                        fontSize: '13px', 
                        textTransform: 'uppercase', 
                        letterSpacing: '1px',
                        color: '#00857c'
                      }}>
                        Navigate to Subfolders
                      </Text>
                    </div>
                    
                    {/* Folder Items */}
                    {folderHierarchy.map((folder) => (
                      <div 
                        key={folder.id}
                        style={{ 
                          padding: '18px 16px',
                          marginBottom: '12px',
                          border: selectedFolder?.id === folder.id ? '3px solid #00857c' : '2px solid #e8e8e8',
                          borderRadius: '10px',
                          cursor: uploading ? 'not-allowed' : 'pointer',
                          backgroundColor: selectedFolder?.id === folder.id ? 'linear-gradient(135deg, #e6f7f6 0%, #d9f0ef 100%)' : 'white',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          boxShadow: selectedFolder?.id === folder.id 
                            ? '0 6px 20px rgba(0, 133, 124, 0.25)' 
                            : '0 2px 8px rgba(0,0,0,0.08)',
                          opacity: uploading ? 0.6 : 1,
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                        onClick={() => !uploading && handleFolderSelect(folder)}
                        onMouseEnter={(e) => {
                          if (!uploading && selectedFolder?.id !== folder.id) {
                            e.currentTarget.style.backgroundColor = '#f0f9f8';
                            e.currentTarget.style.transform = 'translateX(8px) scale(1.02)';
                            e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 133, 124, 0.15)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!uploading && selectedFolder?.id !== folder.id) {
                            e.currentTarget.style.backgroundColor = 'white';
                            e.currentTarget.style.transform = 'translateX(0) scale(1)';
                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                          }
                        }}
                      >
                        {selectedFolder?.id === folder.id && (
                          <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '4px',
                            height: '100%',
                            background: 'linear-gradient(180deg, #00857c 0%, #006b63 100%)'
                          }} />
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                          <div style={{
                            background: selectedFolder?.id === folder.id 
                              ? 'linear-gradient(135deg, #00857c 0%, #006b63 100%)' 
                              : 'linear-gradient(135deg, #4dd0e1 0%, #00acc1 100%)',
                            borderRadius: '12px',
                            padding: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 12px rgba(0, 172, 193, 0.3)',
                            flexShrink: 0
                          }}>
                            <FolderOutlined style={{ 
                              fontSize: '32px', 
                              color: 'white'
                            }} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ 
                              fontWeight: selectedFolder?.id === folder.id ? 700 : 600,
                              color: selectedFolder?.id === folder.id ? '#00857c' : '#333',
                              fontSize: '16px',
                              marginBottom: '6px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {folder.name}
                            </div>
                            {folder.children && folder.children.length > 0 && (
                              <div style={{
                                display: 'inline-block',
                                background: '#e6f7f6',
                                borderRadius: '12px',
                                padding: '4px 10px',
                                fontSize: '11px',
                                fontWeight: 600,
                                color: '#00857c'
                              }}>
                                <FolderOutlined style={{ fontSize: '10px', marginRight: '4px' }} />
                                {folder.children.length} subfolder{folder.children.length !== 1 ? 's' : ''}
                              </div>
                            )}
                          </div>
                          {selectedFolder?.id === folder.id && (
                            <div style={{ 
                              background: 'linear-gradient(135deg, #00857c 0%, #006b63 100%)',
                              color: 'white',
                              fontSize: '20px',
                              fontWeight: 'bold',
                              flexShrink: 0,
                              width: '36px',
                              height: '36px',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 4px 12px rgba(0, 133, 124, 0.4)'
                            }}>
                              <CheckCircleOutlined />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT PANEL - Upload Area */}
            <div style={{ 
              width: '55%',
              background: 'linear-gradient(180deg, #fafbfc 0%, #ffffff 100%)',
              display: 'flex',
              flexDirection: 'column',
              padding: '24px'
            }}>
              {/* Upload Header */}
              <div style={{ marginBottom: '20px' }}>
                <Text strong style={{ color: '#333', fontSize: '18px', display: 'block', marginBottom: '6px' }}>
                  Upload Files
                </Text>
                <Text type="secondary" style={{ fontSize: '13px' }}>
                  Drag and drop or click to select files
                </Text>
              </div>

              {/* Selected Destination Display */}
              {selectedFolder ? (
                <Alert
                  message={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        background: 'linear-gradient(135deg, #00857c 0%, #006b63 100%)',
                        borderRadius: '10px',
                        padding: '10px',
                        display: 'flex'
                      }}>
                        <FolderOutlined style={{ fontSize: '22px', color: 'white' }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, color: '#00857c', marginBottom: '4px', fontSize: '15px' }}>
                          Upload Destination
                        </div>
                        <div style={{ fontSize: '13px', color: '#666' }}>
                          {selectedFolder.path || selectedFolder.name}
                        </div>
                      </div>
                    </div>
                  }
                  type="success"
                  showIcon={false}
                  style={{
                    background: 'linear-gradient(135deg, #e6f7f6 0%, #f0f9f8 100%)',
                    border: '2px solid #b3ddd9',
                    borderRadius: '12px',
                    marginBottom: '20px',
                    padding: '16px',
                    boxShadow: '0 4px 12px rgba(0, 133, 124, 0.1)'
                  }}
                />
              ) : (
                <Alert
                  message={
                    <div style={{ textAlign: 'center', padding: '10px' }}>
                      <div style={{ fontSize: '40px', marginBottom: '8px' }}>
                        <FolderOpenOutlined style={{ color: '#faad14' }} />
                      </div>
                      <div style={{ fontWeight: 600, color: '#faad14', marginBottom: '4px' }}>
                        No Folder Selected
                      </div>
                      <div style={{ fontSize: '13px', color: '#666' }}>
                        Choose a folder from the left panel to continue
                      </div>
                    </div>
                  }
                  type="warning"
                  showIcon={false}
                  style={{
                    marginBottom: '20px',
                    borderRadius: '12px',
                    border: '2px solid #ffe58f'
                  }}
                />
              )}

              {/* File Upload Dragger */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                {selectedFiles.length > 0 ? (
                  <div style={{
                    flex: 1,
                    overflowY: 'auto',
                    background: 'white',
                    border: '2px solid #e0e0e0',
                    borderRadius: '12px',
                    padding: '16px'
                  }}>
                    <div style={{ 
                      marginBottom: '16px',
                      paddingBottom: '12px',
                      borderBottom: '2px solid #e6f7f6',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <Text strong style={{ color: '#00857c', fontSize: '15px' }}>
                        Selected Files ({selectedFiles.length})
                      </Text>
                      <Button 
                        type="link" 
                        danger 
                        size="small"
                        onClick={() => setSelectedFiles([])}
                        style={{ fontSize: '12px' }}
                      >
                        Clear All
                      </Button>
                    </div>
                    <Upload {...uploadProps} />
                  </div>
                ) : (
                  <div style={{
                    flex: 1,
                    background: 'white',
                    border: selectedFolder ? '3px dashed #b3ddd9' : '3px dashed #d9d9d9',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease'
                  }}>
                    <Dragger 
                      {...uploadProps} 
                      disabled={!selectedFolder || uploading}
                      style={{
                        background: selectedFolder 
                          ? 'linear-gradient(135deg, #ffffff 0%, #f0f9f8 100%)' 
                          : '#fafafa',
                        border: 'none',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <div style={{ padding: '60px 20px' }}>
                        <p className="ant-upload-drag-icon">
                          <div style={{
                            background: selectedFolder 
                              ? 'linear-gradient(135deg, #e6f7f6 0%, #d9f0ef 100%)' 
                              : '#f0f0f0',
                            borderRadius: '50%',
                            width: '120px',
                            height: '120px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto',
                            boxShadow: selectedFolder ? '0 8px 24px rgba(0, 133, 124, 0.2)' : 'none'
                          }}>
                            <InboxOutlined style={{ 
                              color: selectedFolder ? '#00857c' : '#d9d9d9', 
                              fontSize: '64px' 
                            }} />
                          </div>
                        </p>
                        <p className="ant-upload-text" style={{ 
                          fontSize: '20px', 
                          fontWeight: 700,
                          color: selectedFolder ? '#00857c' : '#999',
                          marginTop: '24px',
                          marginBottom: '12px'
                        }}>
                          {selectedFolder ? 'Drop files here or click to browse' : 'Select a folder first'}
                        </p>
                        <p className="ant-upload-hint" style={{ 
                          fontSize: '14px', 
                          color: '#666',
                          maxWidth: '400px',
                          margin: '0 auto',
                          lineHeight: '1.6'
                        }}>
                          {selectedFolder
                            ? 'Supported formats: PDF, Documents, Spreadsheets, Images'
                            : 'Navigate through folders to select destination'}
                        </p>
                      </div>
                    </Dragger>
                  </div>
                )}

                {/* Upload Progress */}
                {uploading && (
                  <div style={{
                    background: 'linear-gradient(135deg, #ffffff 0%, #f0f9f8 100%)',
                    padding: '24px',
                    borderRadius: '12px',
                    border: '2px solid #b3ddd9',
                    boxShadow: '0 8px 24px rgba(0, 133, 124, 0.2)',
                    marginTop: '20px'
                  }}>
                    <div style={{ 
                      marginBottom: '16px', 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center' 
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <Spin />
                        <Text strong style={{ color: '#00857c', fontSize: '16px' }}>
                          Uploading to SharePoint...
                        </Text>
                      </div>
                      <Text strong style={{ color: '#00857c', fontSize: '20px' }}>
                        {uploadProgress}%
                      </Text>
                    </div>
                    <Progress
                      percent={uploadProgress}
                      status="active"
                      strokeColor={{
                        '0%': '#00857c',
                        '50%': '#00a097',
                        '100%': '#00c0b3',
                      }}
                      strokeWidth={16}
                      showInfo={false}
                      style={{ borderRadius: '8px' }}
                    />
                    <Text type="secondary" style={{ 
                      fontSize: '12px', 
                      marginTop: '12px', 
                      display: 'block',
                      textAlign: 'center'
                    }}>
                      Please wait while we upload your files securely...
                    </Text>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Modal>

        {/* Create Folder Modal */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                background: 'linear-gradient(135deg, #00857c 0%, #006b63 100%)',
                borderRadius: '10px',
                padding: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <FolderAddOutlined style={{ fontSize: '24px', color: 'white' }} />
              </div>
              <div>
                <div style={{ fontSize: '18px', fontWeight: 700, color: '#333' }}>
                  Create New Folder
                </div>
                <div style={{ fontSize: '13px', color: '#666', marginTop: '2px' }}>
                  Create a subfolder in: {currentPath[currentPath.length - 1]?.name || 'ROBO/Commvault Site Details'}
                </div>
              </div>
            </div>
          }
          open={createFolderModalVisible}
          onCancel={handleCancelCreateFolder}
          footer={null}
          width={600}
          styles={{
            body: { padding: '32px' }
          }}
        >
          <div style={{ marginBottom: '24px' }}>
            <Alert
              message={
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <FolderOutlined style={{ fontSize: '20px', color: '#00857c' }} />
                  <div>
                    <div style={{ fontWeight: 600, color: '#00857c', marginBottom: '2px' }}>
                      Parent Folder
                    </div>
                    <div style={{ fontSize: '13px', color: '#666' }}>
                      {currentPath.map(p => p.name).join(' / ')}
                    </div>
                  </div>
                </div>
              }
              type="info"
              showIcon={false}
              style={{
                background: 'linear-gradient(135deg, #e6f7f6 0%, #f0f9f8 100%)',
                border: '2px solid #b3ddd9',
                borderRadius: '10px'
              }}
            />
          </div>

          <Form layout="vertical">
            <Form.Item
              label={<Text strong style={{ fontSize: '15px' }}>Folder Name</Text>}
              required
              validateStatus={newFolderName && /[<>:"/\\|?*]/.test(newFolderName) ? 'error' : ''}
              help={
                newFolderName && /[<>:"/\\|?*]/.test(newFolderName)
                  ? 'Folder name cannot contain: < > : " / \\ | ? *'
                  : 'Enter a name for the new folder (max 255 characters)'
              }
            >
              <Input
                placeholder="Enter folder name"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                maxLength={255}
                showCount
                size="large"
                prefix={<FolderOutlined style={{ color: '#00857c' }} />}
                onPressEnter={handleCreateFolder}
                autoFocus
                style={{
                  borderRadius: '8px',
                  borderWidth: '2px'
                }}
              />
            </Form.Item>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '32px' }}>
              <Button
                onClick={handleCancelCreateFolder}
                disabled={creatingFolder}
                size="large"
                style={{
                  borderRadius: '8px',
                  height: '48px',
                  padding: '0 32px',
                  fontSize: '15px',
                  fontWeight: 600
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={handleCreateFolder}
                loading={creatingFolder}
                disabled={!newFolderName.trim() || /[<>:"/\\|?*]/.test(newFolderName)}
                icon={<PlusOutlined />}
                size="large"
                style={{
                  background: 'linear-gradient(135deg, #00857c 0%, #006b63 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  height: '48px',
                  padding: '0 32px',
                  fontSize: '15px',
                  fontWeight: 600,
                  boxShadow: '0 4px 12px rgba(0, 133, 124, 0.4)'
                }}
              >
                Create Folder
              </Button>
            </div>
          </Form>
        </Modal>

        {/* Upload Complete Modal */}
        <Modal
          open={uploadComplete}
          onCancel={handleCloseUploadComplete}
          footer={null}
          width={600}
          closable={false}
          centered
          styles={{
            body: { padding: 0 }
          }}
        >
          <div style={{
            background: 'linear-gradient(135deg, #00857c 0%, #006b63 100%)',
            padding: '40px',
            textAlign: 'center',
            color: 'white'
          }}>
            <div style={{
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '50%',
              width: '100px',
              height: '100px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              fontSize: '50px',
              animation: 'bounce 0.6s ease-in-out'
            }}>
              <CheckCircleOutlined style={{ color: 'white' }} />
            </div>
            <h2 style={{ color: 'white', fontSize: '28px', fontWeight: 700, marginBottom: '12px' }}>
              Upload Successful!
            </h2>
            <p style={{ fontSize: '16px', opacity: 0.95, margin: 0 }}>
              Your files have been securely uploaded to SharePoint
            </p>
          </div>
          
          <div style={{ padding: '32px' }}>
            <div style={{ 
              background: 'linear-gradient(135deg, #e6f7f6 0%, #f0f9f8 100%)',
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '24px',
              border: '2px solid #b3ddd9'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-around', textAlign: 'center' }}>
                <div>
                  <div style={{ fontSize: '36px', fontWeight: 700, color: '#00857c', marginBottom: '4px' }}>
                    {uploadSummary.success}
                  </div>
                  <div style={{ fontSize: '14px', color: '#666' }}>Files Uploaded</div>
                </div>
                <div style={{ width: '2px', background: '#b3ddd9' }} />
                <div>
                  <div style={{ fontSize: '36px', fontWeight: 700, color: '#00857c', marginBottom: '4px' }}>
                    {uploadSummary.total}
                  </div>
                  <div style={{ fontSize: '14px', color: '#666' }}>Total Files</div>
                </div>
              </div>
            </div>

            {uploadSummary.files.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <Text strong style={{ color: '#333', fontSize: '15px', display: 'block', marginBottom: '12px' }}>
                  Uploaded Files:
                </Text>
                <div style={{ 
                  maxHeight: '200px', 
                  overflowY: 'auto',
                  background: '#fafafa',
                  borderRadius: '8px',
                  padding: '12px'
                }}>
                  {uploadSummary.files.map((file, index) => (
                    <div key={index} style={{ 
                      padding: '10px 12px',
                      marginBottom: '8px',
                      background: 'white',
                      borderRadius: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      border: '1px solid #e0e0e0'
                    }}>
                      <FileOutlined style={{ fontSize: '20px', color: '#00857c' }} />
                      <span style={{ flex: 1, fontSize: '13px', color: '#333' }}>{file.file_name}</span>
                      <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Button
              type="primary"
              size="large"
              block
              onClick={handleCloseUploadComplete}
              style={{
                background: 'linear-gradient(135deg, #00857c 0%, #006b63 100%)',
                border: 'none',
                height: '50px',
                fontSize: '16px',
                fontWeight: 600,
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0, 133, 124, 0.3)'
              }}
            >
              Done
            </Button>
          </div>
        </Modal>

      {/* Final Buttons */}
        <Row>
          <Col span={24}>
            <Space style={{ marginTop: "35px" }}>
              <Form.Item>
                <Button type="primary">Assessment Complete</Button>
              </Form.Item>
              <Form.Item>
                <Button type="primary">Reviewed</Button>
              </Form.Item>
            </Space>
            <ProcurementStageDetailsFooter/>
          </Col>
        </Row>
      </div>
    </Form>
  );
};

export default SiteSection;
